/**
 * <cw-rail>: keeps the rail's counts and greyed discs in step with the basket, remembers the
 * open aisle for the header's "Categories" link, and shows the phone's search button while the
 * delivery row is tucked away (design-handoff/components/rail.md).
 *
 * Phones: the page scrolls under the shelf (scroll-and-bars.md), and the rail and the aisle's
 * chip bar stick under it. The rail measures the shelf for them: --cw-trim-hide is how far
 * the shelf scrolls before its jars stick (its padding and title row; none where the title is
 * folded for good, as on the product page), --cw-stick-h the height left stuck at the top.
 * Exact (fractional) px: rounded ones left a hairline on Android. ResizeObserver runs after
 * layout and before paint, so the values never trail the shelf by a frame.
 */
import { basketByAisle } from '@cw/aisles';

class CwRail extends HTMLElement {
  connectedCallback() {
    document.addEventListener('cw:cart:change', this.#counts);
    document.addEventListener('cw:fold', this.#fold);
    this.addEventListener('click', this.#onClick);
    const active = this.querySelector('.cw-rail__item.is-active');
    if (active) {
      try {
        sessionStorage.setItem('cw:last-aisle', active.getAttribute('href'));
      } catch {
        // the header falls back to the first aisle
      }
      // Keep the open aisle in view on a long rail (phones in landscape, the 212 rail).
      active.scrollIntoView({ block: 'nearest' });
    }
    this.#counts();
    this.#watchShelf();
  }

  #shelfObserver = null;

  #watchShelf() {
    const band = this.closest('.cw-aisle__body')?.parentElement?.querySelector(':scope > .cw-aisle__band');
    if (!band) return;
    const root = document.documentElement.style;
    const paint = () => {
      const box = band.getBoundingClientRect();
      const trim = band.querySelector('.cw-shelf__trim');
      const t = trim?.getBoundingClientRect();
      const hide = t && t.height > 0 ? t.bottom - box.top : 0;
      root.setProperty('--cw-trim-hide', `${hide}px`);
      root.setProperty('--cw-stick-h', `${box.height - hide}px`);
    };
    paint();
    this.#shelfObserver = new ResizeObserver(paint);
    this.#shelfObserver.observe(band);
  }

  disconnectedCallback() {
    document.removeEventListener('cw:cart:change', this.#counts);
    document.removeEventListener('cw:fold', this.#fold);
    this.removeEventListener('click', this.#onClick);
    this.#shelfObserver?.disconnect();
  }

  #counts = () => {
    const counts = basketByAisle();
    for (const item of this.querySelectorAll('.cw-rail__item')) {
      const qty = counts[item.dataset.aisle] || 0;
      item.classList.toggle('is-empty', qty === 0 && !item.classList.contains('is-active'));
      for (const badge of item.querySelectorAll('[data-count]')) {
        badge.hidden = qty === 0;
        badge.textContent = String(qty);
      }
    }
  };

  #fold = (event) => {
    this.classList.toggle('is-search', !!event.detail?.folded);
  };

  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-open]');
    if (!button) return;
    const request = new CustomEvent(`cw:${button.dataset.open}:open`, { cancelable: true, detail: { source: button } });
    document.dispatchEvent(request);
    if (!request.defaultPrevented) window.location.href = `${window.Shopify?.routes?.root || '/'}search`;
  };
}

if (!customElements.get('cw-rail')) customElements.define('cw-rail', CwRail);
